import { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { wasteMovementsService } from "@/services/wasteMovements/wasteMovements.service";
import { logErrorForDev } from "@/utils";
import {
    dwtConfirmButtonLabel,
    dwtCorrectFeedback,
    dwtCorrectionConfirmButtonLabel,
    dwtCorrectionConfirmTitle,
    dwtRefreshFeedback,
    dwtSubmitFeedback,
} from "../core/dwtSubmissionDisplay";
import { useWasteMovementDwtCorrection } from "../react/useWasteMovementDwtCorrection";
import { useWasteMovementDwtReadiness } from "../react/useWasteMovementDwtReadiness";
import { useWasteMovementDwtSubmission } from "../react/useWasteMovementDwtSubmission";
import { DwtCorrectionForm, dwtCorrectionFormReady } from "./DwtCorrection";
import { DwtSubmissionPanel, DwtSubmitConfirmation, type DwtSubmitFeedback } from "./DwtSubmissionPanel";

export interface DwtSubmissionSectionProps {
    siteId: string | undefined;
    movementId: string | undefined;
    /** The saved record's version (e.g. `updatedAtUtc`); readiness and status are re-read after each save. */
    savedVersion: string | undefined;
    enabled: boolean;
    canSubmit: boolean;
}

/**
 * S2B/S2C/S2D: loads readiness and submission state for a saved Waste Received movement, asks for explicit confirmation, and
 * submits; refreshes an unresolved submission's status with the provider; and corrects an accepted filing (reason, second
 * approver when required, explicit confirmation of which filing it changes). The buttons are disabled while a request is in
 * flight (for the operator's benefit only - the server's database claim is what prevents a second request, and a refresh can
 * never submit). There is no automatic polling: a refresh happens only when the operator asks.
 */
export function DwtSubmissionSection({ siteId, movementId, savedVersion, enabled, canSubmit }: DwtSubmissionSectionProps) {
    const [refresh, setRefresh] = useState(0);
    const readiness = useWasteMovementDwtReadiness(siteId, movementId, `${savedVersion ?? ""}#${refresh}`, enabled);
    const { state: submission, reload, show } = useWasteMovementDwtSubmission(siteId, movementId, savedVersion, enabled);
    const [confirming, setConfirming] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [feedback, setFeedback] = useState<DwtSubmitFeedback | null>(null);

    const loaded = submission?.status === "loaded" ? submission.submission : null;
    const receipt = readiness?.status === "loaded" ? readiness.readiness.receipt ?? null : null;

    // S2D: the correction preview, only while something is filed and the user may change movements.
    const hasFiling = !!loaded?.filingMode;
    const correctionVersion = `${savedVersion ?? ""}#${refresh}#${loaded?.lastAttemptAtUtc ?? ""}#${loaded?.lastCheckedAtUtc ?? ""}#${loaded?.correctionCount ?? 0}`;
    const { state: correction, show: showCorrection } = useWasteMovementDwtCorrection(siteId, movementId, correctionVersion, enabled && canSubmit && hasFiling);
    const correctionLoaded = correction?.status === "loaded" ? correction.correction : null;
    const [correctingOpen, setCorrectingOpen] = useState(false);
    const [correcting, setCorrecting] = useState(false);
    const [reason, setReason] = useState("");
    const [approverId, setApproverId] = useState("");
    const [confirmedCorrection, setConfirmedCorrection] = useState(false);
    const [correctionError, setCorrectionError] = useState<string | null>(null);

    const submit = () => {
        if (!siteId || !movementId || submitting) return;
        setConfirming(false);
        setSubmitting(true);
        setFeedback(null);
        wasteMovementsService
            .submitDwt(siteId, movementId)
            .then(({ httpStatus, body }) => {
                if (body.submission) show(body.submission);
                else reload();
                setFeedback(dwtSubmitFeedback(httpStatus, body));
                // Not ready, or changed while it was being submitted: show the movement as it now stands.
                if (httpStatus !== 200) setRefresh((n) => n + 1);
            })
            .catch((e: unknown) => {
                logErrorForDev("wasteMovement.dwtSubmit", e);
                setFeedback({
                    variant: "danger",
                    message: "The submission request could not be completed. It may have reached the server - its status has been reloaded; check it before trying again.",
                });
                reload();
            })
            .finally(() => setSubmitting(false));
    };

    const refreshStatus = () => {
        if (!siteId || !movementId || refreshing || submitting || correcting) return;
        setRefreshing(true);
        setFeedback(null);
        wasteMovementsService
            .refreshDwtStatus(siteId, movementId)
            .then(({ httpStatus, body }) => {
                if (body.submission) show(body.submission);
                else reload();
                setFeedback(dwtRefreshFeedback(httpStatus, body));
                // A settled status may allow a new submission or a correction: show them as they now stand.
                setRefresh((n) => n + 1);
            })
            .catch((e: unknown) => {
                logErrorForDev("wasteMovement.dwtRefresh", e);
                setFeedback({ variant: "danger", message: "The status could not be checked. Its current state has been reloaded; try again." });
                reload();
            })
            .finally(() => setRefreshing(false));
    };

    const openCorrection = () => {
        setCorrectionError(null);
        setConfirmedCorrection(false);
        setCorrectingOpen(true);
    };

    const correct = () => {
        if (!siteId || !movementId || correcting || !correctionLoaded) return;
        if (!dwtCorrectionFormReady(correctionLoaded, reason, approverId, confirmedCorrection)) return;
        setCorrecting(true);
        setCorrectionError(null);
        setFeedback(null);
        wasteMovementsService
            .correctDwt(siteId, movementId, {
                reason,
                approverIdentityId: correctionLoaded.approvalRequired ? approverId : null,
            })
            .then(({ httpStatus, body }) => {
                if (body.submission) show(body.submission);
                else reload();
                if (body.correction) showCorrection(body.correction);
                const message = dwtCorrectFeedback(httpStatus, body);
                if (httpStatus === 400) {
                    // The reason or approver was not accepted: nothing was sent; let the operator fix it in the dialog.
                    setCorrectionError(message.message);
                    return;
                }
                setFeedback(message);
                setCorrectingOpen(false);
                setConfirmedCorrection(false);
                if (httpStatus === 200) {
                    setReason("");
                    setApproverId("");
                }
                setRefresh((n) => n + 1);
            })
            .catch((e: unknown) => {
                logErrorForDev("wasteMovement.dwtCorrect", e);
                setCorrectingOpen(false);
                setFeedback({
                    variant: "danger",
                    message: "The correction request could not be completed. It may have reached the server - its status has been reloaded; refresh it before correcting again.",
                });
                reload();
            })
            .finally(() => setCorrecting(false));
    };

    if (!enabled) return null;

    const correctionReady = !!correctionLoaded && dwtCorrectionFormReady(correctionLoaded, reason, approverId, confirmedCorrection);

    return (
        <>
            <DwtSubmissionPanel
                readiness={readiness}
                submission={submission}
                submitting={submitting}
                refreshing={refreshing}
                feedback={feedback}
                canSubmit={canSubmit}
                onSubmit={() => setConfirming(true)}
                onRefresh={refreshStatus}
                correction={hasFiling && canSubmit ? correction : null}
                correcting={correcting}
                onCorrect={openCorrection}
            />
            <Modal show={confirming && !!receipt && !!loaded} onHide={() => setConfirming(false)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{loaded?.mode === "live" ? "Confirm LIVE filing with DEFRA" : loaded?.mode === "test" ? "Confirm DEFRA Test submission" : "Confirm dry validation"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {receipt && loaded && (
                        <DwtSubmitConfirmation mode={loaded.mode} receipt={receipt} reference={loaded.reference} provider={loaded.provider} />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setConfirming(false)} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button
                        variant={loaded?.mode === "live" ? "danger" : loaded?.mode === "test" ? "warning" : "primary"}
                        onClick={submit}
                        disabled={submitting}
                        data-testid="dwt-confirm-button"
                    >
                        {dwtConfirmButtonLabel(loaded?.mode)}
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal show={correctingOpen && !!correctionLoaded} onHide={() => !correcting && setCorrectingOpen(false)} centered size="lg">
                <Modal.Header closeButton={!correcting}>
                    <Modal.Title>{dwtCorrectionConfirmTitle(correctionLoaded?.mode)}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {correctionLoaded && (
                        <DwtCorrectionForm
                            correction={correctionLoaded}
                            reason={reason}
                            onReasonChange={setReason}
                            approverId={approverId}
                            onApproverChange={setApproverId}
                            confirmed={confirmedCorrection}
                            onConfirmedChange={setConfirmedCorrection}
                            error={correctionError ?? (correctionLoaded.available ? null : correctionLoaded.message)}
                            disabled={correcting}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setCorrectingOpen(false)} disabled={correcting}>
                        Cancel
                    </Button>
                    <Button
                        variant={correctionLoaded?.mode === "live" ? "danger" : "warning"}
                        onClick={correct}
                        disabled={correcting || !correctionReady}
                        data-testid="dwt-correction-confirm-button"
                    >
                        {correcting ? "Correcting…" : dwtCorrectionConfirmButtonLabel(correctionLoaded?.mode)}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
